import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { ChevronDown, Download, ShieldCheck, Smartphone } from "lucide-react";
import type { LatestAppReleaseResponse } from "@mubin/contracts";
import { api } from "../lib/api";

/**
 * The public download page (owner request 2026-10-21): the newest Android app, always at the same link.
 * Students and teachers ask to join from inside the app, so /join leads here too. iPhone users install it
 * from the App Store, which approved version 1.0 (2026-10-30; TestFlight before that).
 */
const IOS_APP_STORE = "https://apps.apple.com/app/id6821867000";

export function DownloadPage() {
  const latest = useQuery({
    queryKey: ["app-release"],
    queryFn: () => api<LatestAppReleaseResponse>("/v1/app-releases/android/latest"),
  });
  const r = latest.data?.release;
  return (
    <main className="dl-page">
      <section className="dl-hero">
        <img src="/logo-dark.png" alt="مُبين" className="dl-logo" />
        <h1>حمّل تطبيق مُبين</h1>
        <p>
          تسميع القرآن الكريم مع معلمك من جوالك، ومتابعة وردك وحضورك ونتائجك. ومن التطبيق تقدّم طلب الانضمام
          طالبًا أو معلمًا متطوعًا.
        </p>
        <div className="dl-buttons">
          {r ? (
            <a className="dl-button" href={r.downloadPath}>
              <Download size={22} strokeWidth={1.75} />
              <span>
                تحميل للأندرويد
                <small>
                  الإصدار <span className="ltr">{r.versionName}</span> · {Math.round(r.size / 1024 / 1024)}{" "}
                  ميجابايت
                </small>
              </span>
            </a>
          ) : latest.isPending ? null : (
            <p className="dl-soon">التطبيق غير متاح للتحميل الآن، عد قريبًا.</p>
          )}
          <a className="dl-button dl-button-ios" href={IOS_APP_STORE} target="_blank" rel="noreferrer">
            <Smartphone size={22} strokeWidth={1.75} />
            <span>
              تحميل للآيفون
              <small>من App Store</small>
            </span>
          </a>
        </div>
      </section>
      <section className="dl-card" id="install">
        <InstallCue />
        <h2>طريقة التثبيت على أندرويد</h2>
        <ol>
          <li>اضغط «تحميل للأندرويد» وانتظر اكتمال التحميل.</li>
          <li>افتح الملف من الإشعارات أو من مجلد التنزيلات.</li>
          <li>إن طلب الجوال السماح بالتثبيت من هذا المصدر، فاسمح ثم ارجع واضغط «تثبيت».</li>
          <li>افتح مُبين وادخل برقم جوالك، أو قدّم طلب انضمام.</li>
        </ol>
        {r?.notes && (
          <p className="muted small">
            <b>ما الجديد:</b> {r.notes}
          </p>
        )}
        <h2>طريقة التثبيت على الآيفون</h2>
        <ol>
          <li>اضغط «تحميل للآيفون» فتفتح صفحة مُبين في App Store.</li>
          <li>اضغط «تحميل»، ثم افتح مُبين وادخل برقم جوالك، أو قدّم طلب انضمام.</li>
        </ol>
        <p className="muted small dl-safe">
          <ShieldCheck size={16} strokeWidth={1.75} /> التطبيق من الجمعية الخيرية لتحفيظ القرآن الكريم بمحافظة
          رماح، ويُحدَّث تلقائيًا بعد التثبيت.
        </p>
      </section>
      <footer className="dl-footer">
        <img src="/org/horizontal-color.png" alt="الجمعية الخيرية لتحفيظ القرآن الكريم بمحافظة رماح" />
        <nav className="legal-links small">
          <Link to="/privacy">سياسة الخصوصية</Link>
          <Link to="/terms">شروط الاستخدام</Link>
        </nav>
      </footer>
    </main>
  );
}

/**
 * A glowing round button on the top edge of the install steps, gently bobbing, with a golden shimmer along
 * the card's edge: the steps are read before downloading (owner's choice, 2026-10-23). It adds no space.
 */
function InstallCue() {
  return (
    <a
      className="dl-cue"
      href="#install"
      aria-label="طريقة التثبيت"
      onClick={(e) => {
        e.preventDefault();
        document.getElementById("install")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }}
    >
      <ChevronDown size={22} strokeWidth={2.4} aria-hidden="true" />
    </a>
  );
}
